import { lazy } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

// project import
import MainLayout from 'layout/MainLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'guards/AuthGuard';
import { APP_DEFAULT_PATH } from 'config';

const Dashboard = Loadable(lazy(() => import('pages/dashboard')));

// Prompt Library
const PromptLibrary = Loadable(lazy(() => import('pages/prompt-library')));
const AddPrompt = Loadable(lazy(() => import('pages/prompt-library/add-prompt')));

// Requisition
const ListRequisitions = Loadable(lazy(() => import('pages/requisitions')));
const AddRequisition = Loadable(lazy(() => import('pages/requisitions/add-requisition')));

// Video Question
const ListVideoQuestions = Loadable(lazy(() => import('pages/video-questions')));
const AddVideoQuestion = Loadable(lazy(() => import('pages/video-questions/add-video-question')));

// Video Question Results
const VideoQuestionResults = Loadable(lazy(() => import('pages/video-question-results')));
const ViewVideoQuestionResult = Loadable(lazy(() => import('pages/video-question-results/view')));

// Video Question Test Results
const VideoQuestionTestResults = Loadable(lazy(() => import('pages/video-question-test-results')));
const ViewVideoQuestionTestResult = Loadable(lazy(() => import('pages/video-question-test-results/view')));

// Video Question Profile
const ListVideoQuestionProfiles = Loadable(lazy(() => import('pages/video-question-profiles')));
const AddVideoQuestionProfile = Loadable(lazy(() => import('pages/video-question-profiles/add-video-question-profile')));

// Qualification Question Profile
const ListQualificationQuestionProfiles = Loadable(lazy(() => import('pages/qualification-question-profiles')));
const AddQualificationQuestionProfile = Loadable(
  lazy(() => import('pages/qualification-question-profiles/add-qualification-question-profile'))
);

// Typing Test
const ListTypingTests = Loadable(lazy(() => import('pages/typing-tests')));
const AddTypingTest = Loadable(lazy(() => import('pages/typing-tests/add-typing-test')));

// Resume Profile
const ListResumeProfiles = Loadable(lazy(() => import('pages/resume-profiles')));
const AddResumeProfile = Loadable(lazy(() => import('pages/resume-profiles/add-resume-profile')));

// Resume Screen Results
const ResumeScreenResults = Loadable(lazy(() => import('pages/resume-screen-results')));
const ViewResumeScreenResult = Loadable(lazy(() => import('pages/resume-screen-results/view')));

// Resume Screen Test Results
const ResumeScreenTestResults = Loadable(lazy(() => import('pages/resume-screen-test-results')));
const ViewResumeScreenTestResult = Loadable(lazy(() => import('pages/resume-screen-test-results/view')));

// Qualification Question
const ListQualificationQuestions = Loadable(lazy(() => import('pages/qualification-questions')));
const AddQualificationQuestion = Loadable(lazy(() => import('pages/qualification-questions/add-qualification-question')));

// Typing Assessment Results
const TypingAssessmentResults = Loadable(lazy(() => import('pages/typing-assessment-results')));
const ViewTypingAssessmentResult = Loadable(lazy(() => import('pages/typing-assessment-results/view')));

// Typing Assessment Test Results
const TypingAssessmentTestResults = Loadable(lazy(() => import('pages/typing-assessment-test-results')));
const ViewTypingAssessmentTestResult = Loadable(lazy(() => import('pages/typing-assessment-test-results/view')));

// Applicant Tracker
const ApplicantTracker = Loadable(lazy(() => import('pages/applicant-tracker')));
const NewApplicantTracker = Loadable(lazy(() => import('pages/new-applicant-tracker')));
const ReviewApplicantResults = Loadable(lazy(() => import('pages/new-applicant-tracker/review-results')));

// User Management
const UserManagement = Loadable(lazy(() => import('pages/user-management')));

// Lines of Business
const LOBs = Loadable(lazy(() => import('pages/lines-of-business')));

// Roles & Permissions
const RolesAndPermissions = Loadable(lazy(() => import('pages/roles-and-permissions')));

// Archived Items
const ArchivedItems = Loadable(lazy(() => import('pages/archived-items')));

// Class Requests
const ClassRequestsTracker = Loadable(lazy(() => import('pages/class-requests')));
const ViewClassRequest = Loadable(lazy(() => import('pages/class-requests/view')));

const redirects = {
  'talent-acquisition': 'talent-discovery'
};

const Redirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace(RegExp(Object.keys(redirects).join('|'), 'gi'), (match) => redirects[match]);
  return <Navigate to={newPath} replace />;
};

const MainRoutes = {
  path: '/',
  element: (
    <AuthGuard>
      <MainLayout />
    </AuthGuard>
  ),
  children: [
    {
      index: true,
      element: <Navigate to={APP_DEFAULT_PATH} />
    },
    {
      path: 'dashboard',
      element: <Dashboard />
    },
    {
      path: 'clients',
      children: [
        {
          path: '',
          element: <Navigate to="lines-of-business" replace />
        },
        {
          path: 'lines-of-business',
          element: <LOBs />
        }
      ]
    },
    {
      path: 'settings',
      children: [
        {
          path: '',
          element: <Navigate to="user-management" replace />
        },
        {
          path: 'user-management',
          element: <UserManagement />
        },
        {
          path: 'roles-and-permissions',
          element: <RolesAndPermissions />,
          handle: {
            breadcrumb: 'Roles & Permissions'
          }
        }
      ]
    },
    {
      path: 'talent-discovery',
      children: [
        {
          path: '',
          element: <Navigate to="requisitions" replace />
        },
        {
          path: 'requisitions',
          element: <ListRequisitions />
        },
        {
          path: 'requisitions/:requisitionId',
          element: <AddRequisition />,
          handle: {
            breadcrumb: 'Requisition Builder'
          }
        },
        {
          path: 'applicant-tracker',
          element: <ApplicantTracker />,
          handle: {
            breadcrumb: 'Manual Applicant Tracker'
          }
        },
        {
          path: 'req-applicant-tracker',
          element: <NewApplicantTracker />,
          handle: {
            breadcrumb: 'Requisition Applicant Tracker'
          }
        },
        {
          path: 'req-applicant-tracker/:id',
          element: <ReviewApplicantResults />,
          handle: {
            breadcrumbLoading: true
          }
        },
        {
          path: 'video-questions',
          element: <ListVideoQuestions />
        },
        {
          path: 'video-questions/:videoQuestionId',
          element: <AddVideoQuestion />,
          handle: {
            breadcrumb: 'Video Question Builder'
          }
        },
        {
          path: 'video-question-results',
          element: <VideoQuestionResults />
        },
        {
          path: 'video-question-results/:id',
          element: <ViewVideoQuestionResult />,
          handle: {
            breadcrumbLoading: true
          }
        },
        {
          path: 'video-question-profiles',
          element: <ListVideoQuestionProfiles />
        },
        {
          path: 'video-question-profiles/:videoQuestionProfileId',
          element: <AddVideoQuestionProfile />,
          handle: {
            breadcrumb: 'Video Question Profile Builder'
          }
        },
        {
          path: 'qualification-question-profiles',
          element: <ListQualificationQuestionProfiles />
        },
        {
          path: 'qualification-question-profiles/:qualificationQuestionProfileId',
          element: <AddQualificationQuestionProfile />,
          handle: {
            breadcrumb: 'Qualification Question Profile Builder'
          }
        },
        {
          path: 'typing-tests',
          element: <ListTypingTests />
        },
        {
          path: 'typing-tests/:typingTestId',
          element: <AddTypingTest />,
          handle: {
            breadcrumb: 'Typing Test Builder'
          }
        },
        {
          path: 'resume-profiles',
          element: <ListResumeProfiles />
        },
        {
          path: 'resume-profiles/:resumeProfileId',
          element: <AddResumeProfile />,
          handle: {
            breadcrumb: 'Resume Profile Builder'
          }
        },
        {
          path: 'resume-screen-results',
          element: <ResumeScreenResults />
        },
        {
          path: 'resume-screen-results/:id',
          element: <ViewResumeScreenResult />,
          handle: {
            breadcrumbLoading: true
          }
        },
        {
          path: 'typing-test-results',
          element: <TypingAssessmentResults />
        },
        {
          path: 'typing-test-results/:id',
          element: <ViewTypingAssessmentResult />,
          handle: {
            breadcrumbLoading: true
          }
        },
        {
          path: 'qualification-questions',
          element: <ListQualificationQuestions />
        },
        {
          path: 'qualification-questions/:qualificationQuestionId',
          element: <AddQualificationQuestion />,
          handle: {
            breadcrumb: 'Qualification Question Builder'
          }
        },
        {
          path: 'class-requests',
          element: <ClassRequestsTracker />,
          handle: {
            breadcrumb: 'Class Requests Tracker'
          }
        },
        {
          path: 'class-requests/:id',
          element: <ViewClassRequest />,
          handle: {
            breadcrumbLoading: true
          }
        }
      ]
    },
    {
      path: 'testing-results',
      children: [
        {
          path: '',
          element: <Navigate to="video-question-results" replace />
        },
        {
          path: 'video-question-results',
          element: <VideoQuestionTestResults />
        },
        {
          path: 'video-question-results/:id',
          element: <ViewVideoQuestionTestResult />,
          handle: {
            breadcrumbLoading: true
          }
        },
        {
          path: 'resume-screen-results',
          element: <ResumeScreenTestResults />
        },
        {
          path: 'resume-screen-results/:id',
          element: <ViewResumeScreenTestResult />,
          handle: {
            breadcrumbLoading: true
          }
        },
        {
          path: 'typing-test-results',
          element: <TypingAssessmentTestResults />
        },
        {
          path: 'typing-test-results/:id',
          element: <ViewTypingAssessmentTestResult />,
          handle: {
            breadcrumbLoading: true
          }
        }
      ]
    },
    {
      path: 'generative-ai',
      children: [
        {
          path: '',
          element: <Navigate to="prompt-library" replace />
        },
        {
          path: 'prompt-library',
          element: <PromptLibrary />
        },
        {
          path: 'prompt-library/:promptId',
          element: <AddPrompt />,
          handle: {
            breadcrumbLoading: true
          }
        }
      ]
    },
    {
      path: 'archived-items',
      element: <Outlet />,
      children: [
        {
          path: '',
          element: <ArchivedItems />,
          handle: {
            breadcrumb: false
          }
        },
        {
          path: 'applicant-tracker',
          element: <ApplicantTracker />,
          handle: {
            breadcrumb: 'Manual Applicant Tracker (Archived)'
          }
        },
        {
          path: 'req-applicant-tracker',
          element: <NewApplicantTracker />,
          handle: {
            breadcrumb: 'Requisition Applicant Tracker (Archived)'
          }
        },
        {
          path: 'req-applicant-tracker/:id',
          element: <ReviewApplicantResults />,
          handle: {
            breadcrumbLoading: true
          }
        },
        {
          path: 'video-question-results',
          element: <VideoQuestionResults />,
          handle: {
            breadcrumb: 'Video Question Results (Archived)'
          }
        },
        {
          path: 'video-question-results/:id',
          element: <ViewVideoQuestionResult />,
          handle: {
            breadcrumbLoading: true
          }
        },
        {
          path: 'resume-screen-results',
          element: <ResumeScreenResults />,
          handle: {
            breadcrumb: 'Resume Screen Results (Archived)'
          }
        },
        {
          path: 'resume-screen-results/:id',
          element: <ViewResumeScreenResult />,
          handle: {
            breadcrumbLoading: true
          }
        },
        {
          path: 'typing-test-results',
          element: <TypingAssessmentResults />,
          handle: {
            breadcrumb: 'Typing Test Results (Archived)'
          }
        },
        {
          path: 'typing-test-results/:id',
          element: <ViewTypingAssessmentResult />,
          handle: {
            breadcrumbLoading: true
          }
        },
        {
          path: 'testing-results',
          children: [
            {
              path: '',
              element: <Navigate to="video-question-results" replace />
            },
            {
              path: 'video-question-results',
              element: <VideoQuestionTestResults />,
              handle: {
                breadcrumb: 'Video Question Results (Archived)'
              }
            },
            {
              path: 'video-question-results/:id',
              element: <ViewVideoQuestionTestResult />,
              handle: {
                breadcrumbLoading: true
              }
            },
            {
              path: 'resume-screen-results',
              element: <ResumeScreenTestResults />,
              handle: {
                breadcrumb: 'Resume Screen Results (Archived)'
              }
            },
            {
              path: 'resume-screen-results/:id',
              element: <ViewResumeScreenTestResult />,
              handle: {
                breadcrumbLoading: true
              }
            },
            {
              path: 'typing-test-results',
              element: <TypingAssessmentTestResults />,
              handle: {
                breadcrumb: 'Typing Test Results (Archived)'
              }
            },
            {
              path: 'typing-test-results/:id',
              element: <ViewTypingAssessmentTestResult />,
              handle: {
                breadcrumbLoading: true
              }
            }
          ]
        }
      ]
    },
    {
      path: 'talent-acquisition/*',
      element: <Redirect />
    }
  ]
};

export default MainRoutes;
